import { useLoaderData } from 'react-router';

import useVariantPrice from '@modules/product/hook/use-variant-price';
import { buildPriceRangeString } from '@modules/product/component/variant-pricing/VariantPricing';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useMeasuredPriceUnit } from '@modules/product/hook/use-measured-price-unit';
import type { loader } from '~/routes/_app.product.$urlSegment';

export interface ProductPrice {
    /** The price as shown, or null when this reader may not see one. */
    priceString: string | null;
    /** Struck-out retail price on a sale, otherwise null. */
    retailPriceString: string | null;
    /** "From" / "Up to" on a range, otherwise null. */
    pricePrefix: string | null;
    isPriceRange: boolean;
    /** On sale, and allowed to be shown as such. */
    isOnSale: boolean;
    /** Prefix and price as one line, e.g. "From $4,295". */
    label: string | null;
}

/**
 * The product's price, gated the way the legacy page gates it.
 *
 * Shared rather than derived twice: the purchase block, the drawer, the island
 * and the spec list all show the same figure, and two derivations of a gated
 * price is how one surface ends up publishing what another withholds.
 *
 * Who may see a price: a professional can switch pricing off for their whole
 * catalogue, and trade / specialty pricing is gated per user — a guest on a
 * trade-only variant gets `CanViewPrice: false` while `Price` still carries the
 * raw amount, so reading the amount alone would publish it.
 *
 * Requires `VariantSelectionProvider` + `VariantOptionProvider` above it.
 */
export const useProductPrice = (): ProductPrice => {
    const { product } = useLoaderData<typeof loader>();
    const { price, maxPrice, isPriceRange, isOnSale, retailPrice } =
        useVariantPrice(product.Pricing.PricingType);
    const { selectedVariant } = useVariantSelectionState();
    const { unitLabel } = useMeasuredPriceUnit();

    const showMaxPrice =
        isPriceRange &&
        maxPrice.Amount > 0 &&
        price.Amount > 0 &&
        maxPrice.Amount !== price.Amount;
    const priceToShow = isPriceRange
        ? price.Amount > 0
            ? price
            : maxPrice
        : price;

    const canShowPrice =
        !product.Professional.PricingDisabled &&
        Boolean(selectedVariant?.CanViewPrice);
    const amountString =
        canShowPrice && priceToShow.Amount > 0
            ? buildPriceRangeString(
                  priceToShow.ShortFormat,
                  maxPrice.ShortFormat,
                  showMaxPrice,
              )
            : null;
    // A rate reads as one: "$4.65 / sq ft" rather than a bare figure.
    const priceString =
        amountString && unitLabel
            ? `${amountString} / ${unitLabel}`
            : amountString;

    const retailPriceString =
        canShowPrice && isOnSale && !isPriceRange && retailPrice.Amount > 0
            ? retailPrice.ShortFormat.replace(/\.00$/, '')
            : null;

    // Matching legacy VariantPricing: "From" when the range has a lower bound,
    // "Up to" when only a max is set.
    const pricePrefix = isPriceRange
        ? price.Amount > 0
            ? 'From'
            : 'Up to'
        : null;

    return {
        priceString,
        retailPriceString,
        pricePrefix,
        isPriceRange,
        isOnSale: canShowPrice && isOnSale,
        label: priceString
            ? [pricePrefix, priceString].filter(Boolean).join(' ')
            : null,
    };
};
